
import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle, Download, Link as LinkIcon, Star, File, Video, Clock, Mail, Phone, User, X, FileText, Calendar, BarChart, MessageSquareText } from 'lucide-react';
import ScheduleInterviewDialog from './ScheduleInterviewDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import CandidateAssessmentDetails from './CandidateAssessmentDetails';
import { getCandidateAssessment } from '../utils/assessmentEvaluation';
import { getAssessmentQuestions } from '../utils/assessmentQuestions';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';

interface VideoSummary {
  keyPoints: string[];
  impressions: string[];
  overallRating: number;
  communicationScore: number;
  confidenceScore: number;
  enthusiasmScore: number;
  recommendationText: string;
}

interface Candidate {
  id: string;
  name: string;
  email: string;
  phone: string;
  jobTitle: string;
  experience: string;
  currentCtc: string;
  expectedCtc: string;
  resumeUrl: string;
  videoUrl: string;
  appliedDate: string;
  status: 'new' | 'reviewed' | 'shortlisted' | 'rejected';
  skills: string[];
  resumeSummary: string;
  assessmentScore: number;
  videoSummary?: VideoSummary;
}

interface CandidateCardProps {
  candidate: Candidate;
  onStatusChange: (id: string, status: Candidate['status']) => void;
}

const CandidateCard = ({ candidate, onStatusChange }: CandidateCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const [isAssessmentOpen, setIsAssessmentOpen] = useState(false);
  const [isVideoSummaryOpen, setIsVideoSummaryOpen] = useState(false);

  const assessment = getCandidateAssessment(candidate.id);
  
  const { mcqs, descriptive } = getAssessmentQuestions();

  const getStatusColor = (status: Candidate['status']) => {
    switch (status) {
      case 'new':
      case 'reviewed':
        return 'bg-blue-50 text-blue-600';
      case 'shortlisted':
        return 'bg-green-50 text-green-600';
      case 'rejected':
        return 'bg-red-50 text-red-600';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };

  const getStatusIcon = (status: Candidate['status']) => {
    switch (status) {
      case 'new':
      case 'reviewed':
        return <Clock size={14} />;
      case 'shortlisted':
        return <Star size={14} />;
      case 'rejected':
        return <X size={14} />;
      default:
        return null;
    }
  };

  const getStatusDisplayText = (status: Candidate['status']): string => {
    switch (status) {
      case 'new':
      case 'reviewed':
        return 'Pending Review';
      case 'shortlisted':
        return 'Shortlisted';
      case 'rejected':
        return 'Rejected';
      default:
        const statusStr = String(status);
        return statusStr.charAt(0).toUpperCase() + statusStr.slice(1);
    }
  };

  const handleScheduleInterview = () => {
    setIsSchedulerOpen(true);
  };

  // Helper function to get color based on score
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-blue-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  // Helper function to get progress color based on score
  const getProgressColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 75) return 'bg-blue-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white hover:shadow-md transition-all duration-300">
      <div className="p-5">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex items-center">
              <h3 className="text-lg font-semibold text-apple-text mr-3">{candidate.name}</h3>
              <span 
                className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center ${getStatusColor(candidate.status)}`}
              >
                <span className="mr-1">{getStatusIcon(candidate.status)}</span>
                <span>{getStatusDisplayText(candidate.status)}</span>
              </span>
            </div>
            <p className="text-apple-text-secondary">{candidate.jobTitle}</p>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => setExpanded(!expanded)}
              className="px-3 py-1.5 text-sm font-medium text-apple-blue transition-colors hover:text-opacity-80"
            >
              {expanded ? 'View Less' : 'View More'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 mb-4">
          <div>
            <div className="text-xs text-apple-text-secondary mb-1">Experience</div>
            <div className="font-medium text-apple-text">{candidate.experience}</div>
          </div>
          <div>
            <div className="text-xs text-apple-text-secondary mb-1">Current CTC</div>
            <div className="font-medium text-apple-text">{candidate.currentCtc}</div>
          </div>
          <div>
            <div className="text-xs text-apple-text-secondary mb-1">Expected CTC</div>
            <div className="font-medium text-apple-text">{candidate.expectedCtc}</div>
          </div>
          <div>
            <div className="text-xs text-apple-text-secondary mb-1">Assessment Score</div>
            <div className="font-medium text-apple-text">{candidate.assessmentScore}%</div>
          </div>
        </div>

        <div className="mb-4">
          <div className="text-xs text-apple-text-secondary mb-2">Skills</div>
          <div className="flex flex-wrap gap-2">
            {candidate.skills.map((skill, index) => (
              <span 
                key={index}
                className="bg-gray-100 text-apple-text-secondary px-2.5 py-1 rounded-full text-xs font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <a 
            href={candidate.resumeUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-apple-text transition-all hover:bg-gray-50 flex items-center"
          >
            <FileText size={14} className="mr-1.5" />
            Resume
          </a>
          <a 
            href={candidate.videoUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-apple-text transition-all hover:bg-gray-50 flex items-center"
          >
            <Video size={14} className="mr-1.5" />
            Video
          </a>
          <button 
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-apple-text transition-all hover:bg-gray-50 flex items-center"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/candidate/${candidate.id}`);
              toast.success('Candidate link copied to clipboard');
            }}
          >
            <LinkIcon size={14} className="mr-1.5" />
            Share
          </button>

          <Dialog open={isAssessmentOpen} onOpenChange={setIsAssessmentOpen}>
            <DialogTrigger asChild>
              <button 
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-apple-text transition-all hover:bg-gray-50 flex items-center"
              >
                <BarChart size={14} className="mr-1.5" />
                Assessment
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Assessment Results: {candidate.name}</DialogTitle>
              </DialogHeader>
              {assessment ? (
                <CandidateAssessmentDetails 
                  assessment={assessment}
                  mcqQuestions={mcqs}
                  descriptiveQuestions={descriptive}
                />
              ) : (
                <div className="py-10 text-center">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-semibold text-gray-900">No assessment data</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This candidate hasn't completed an assessment yet.
                  </p>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Video Summary Dialog */}
          <Dialog open={isVideoSummaryOpen} onOpenChange={setIsVideoSummaryOpen}>
            <DialogTrigger asChild>
              <button 
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-apple-text transition-all hover:bg-gray-50 flex items-center"
              >
                <MessageSquareText size={14} className="mr-1.5" />
                Video Summary
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Video Introduction Analysis: {candidate.name}</DialogTitle>
              </DialogHeader>

              {candidate.videoSummary ? (
                <div className="space-y-6 pt-4">
                  <div>
                    <h3 className="text-lg font-medium mb-3">Overall Rating</h3>
                    <div className="flex justify-between mb-1 text-sm">
                      <span>Score: {candidate.videoSummary.overallRating}/100</span>
                      <span className={getScoreColor(candidate.videoSummary.overallRating)}>{
                        candidate.videoSummary.overallRating >= 90 ? 'Excellent' :
                        candidate.videoSummary.overallRating >= 75 ? 'Good' :
                        candidate.videoSummary.overallRating >= 60 ? 'Average' : 'Poor'
                      }</span>
                    </div>
                    <Progress 
                      value={candidate.videoSummary.overallRating} 
                      className="h-2" 
                      indicatorClassName={getProgressColor(candidate.videoSummary.overallRating)} 
                    />
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div className="border p-3 rounded-lg">
                        <div className="text-sm text-gray-500 mb-1">Communication</div>
                        <div className="flex justify-between items-center">
                          <span className={`text-lg font-medium ${getScoreColor(candidate.videoSummary.communicationScore)}`}>
                            {candidate.videoSummary.communicationScore}/100
                          </span>
                          <Progress 
                            value={candidate.videoSummary.communicationScore} 
                            className="h-1.5 w-20" 
                            indicatorClassName={getProgressColor(candidate.videoSummary.communicationScore)} 
                          />
                        </div>
                      </div>
                      
                      <div className="border p-3 rounded-lg">
                        <div className="text-sm text-gray-500 mb-1">Confidence</div>
                        <div className="flex justify-between items-center">
                          <span className={`text-lg font-medium ${getScoreColor(candidate.videoSummary.confidenceScore)}`}>
                            {candidate.videoSummary.confidenceScore}/100
                          </span>
                          <Progress 
                            value={candidate.videoSummary.confidenceScore} 
                            className="h-1.5 w-20" 
                            indicatorClassName={getProgressColor(candidate.videoSummary.confidenceScore)} 
                          />
                        </div>
                      </div>
                      
                      <div className="border p-3 rounded-lg">
                        <div className="text-sm text-gray-500 mb-1">Enthusiasm</div>
                        <div className="flex justify-between items-center">
                          <span className={`text-lg font-medium ${getScoreColor(candidate.videoSummary.enthusiasmScore)}`}>
                            {candidate.videoSummary.enthusiasmScore}/100
                          </span>
                          <Progress 
                            value={candidate.videoSummary.enthusiasmScore} 
                            className="h-1.5 w-20" 
                            indicatorClassName={getProgressColor(candidate.videoSummary.enthusiasmScore)} 
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-medium mb-3">Key Points Mentioned</h3>
                      <ul className="space-y-2">
                        {candidate.videoSummary.keyPoints.map((point, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-medium mb-3">Impressions</h3>
                      <ul className="space-y-2">
                        {candidate.videoSummary.impressions.map((impression, index) => (
                          <li key={index} className="flex items-start">
                            <span className="h-2 w-2 rounded-full bg-blue-500 mr-2 flex-shrink-0 mt-2" />
                            <span>{impression}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium mb-2">Recommendation</h3>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p>{candidate.videoSummary.recommendationText}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-10 text-center">
                  <Video className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-semibold text-gray-900">No video analysis</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This candidate's video hasn't been analyzed yet.
                  </p>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {candidate.status === 'shortlisted' && (
            <button 
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-apple-text transition-all hover:bg-gray-50 flex items-center"
              onClick={handleScheduleInterview}
            >
              <Calendar size={14} className="mr-1.5" />
              Schedule Interview
            </button>
          )}
        </div>

        {expanded && (
          <div className="mt-5 pt-5 border-t border-gray-200 animate-fade-in">
            <div className="mb-4">
              <div className="text-sm font-medium text-apple-text mb-2">Contact Information</div>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <Mail className="text-apple-text-secondary mr-2" size={16} />
                  <a 
                    href={`mailto:${candidate.email}`} 
                    className="text-apple-blue hover:underline"
                  >
                    {candidate.email}
                  </a>
                </div>
                <div className="flex items-center text-sm">
                  <Phone className="text-apple-text-secondary mr-2" size={16} />
                  <a 
                    href={`tel:${candidate.phone}`} 
                    className="text-apple-blue hover:underline"
                  >
                    {candidate.phone}
                  </a>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <div className="text-sm font-medium text-apple-text mb-2">AI Resume Summary</div>
              <p className="text-sm text-apple-text">{candidate.resumeSummary}</p>
            </div>

            <div className="mb-4">
              <div className="text-sm font-medium text-apple-text mb-2">Update Status</div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onStatusChange(candidate.id, 'new')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center ${
                    candidate.status === 'new' || candidate.status === 'reviewed'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-apple-text-secondary hover:bg-gray-200'
                  }`}
                >
                  <Clock size={14} className="mr-1.5" />
                  Pending Review
                </button>
                <button
                  onClick={() => onStatusChange(candidate.id, 'shortlisted')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center ${
                    candidate.status === 'shortlisted'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-apple-text-secondary hover:bg-gray-200'
                  }`}
                >
                  <Star size={14} className="mr-1.5" />
                  Shortlisted
                </button>
                <button
                  onClick={() => onStatusChange(candidate.id, 'rejected')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center ${
                    candidate.status === 'rejected'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-apple-text-secondary hover:bg-gray-200'
                  }`}
                >
                  <X size={14} className="mr-1.5" />
                  Rejected
                </button>
              </div>
            </div>

            <div className="text-xs text-apple-text-secondary">
              Applied on {candidate.appliedDate}
            </div>
          </div>
        )}
      </div>
      
      <ScheduleInterviewDialog 
        isOpen={isSchedulerOpen}
        onClose={() => setIsSchedulerOpen(false)}
        candidate={{
          name: candidate.name,
          email: candidate.email
        }}
      />
    </div>
  );
};

export default CandidateCard;
